import { STORAGE_THEME } from "@weco/suite-client";

export type SuiteTheme = "dark" | "light" | "system";

/** Resolved theme applied to `document.documentElement.dataset.theme`. */
export type EffectiveSuiteTheme = "dark" | "light";

/**
 * Dark mode is hidden for now. While this is false every theme resolves to
 * light and the theme controls are not rendered (topbar, Settings,
 * Preferences). A user's stored choice is left untouched, so flipping this back
 * to true restores whatever each person had picked. Keep `index.html`'s
 * pre-paint script in step with it -- it runs before this module loads.
 */
export const DARK_MODE_AVAILABLE = false;

export function resolveEffectiveTheme(stored: SuiteTheme): EffectiveSuiteTheme {
  if (!DARK_MODE_AVAILABLE) return "light";
  if (stored === "system") {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "dark";
  }
  return stored;
}

/**
 * Read theme preference from localStorage (may be `system`), apply resolved value to DOM.
 * Returns the stored preference (including `system`) for React state.
 */
export function initSuiteThemeFromStorage(): SuiteTheme {
  const raw =
    (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_THEME)) || "dark";
  const stored: SuiteTheme =
    raw === "light" || raw === "dark" || raw === "system" ? raw : "dark";
  const effective = resolveEffectiveTheme(stored);
  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = effective;
    if (effective === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }
  return stored;
}

/**
 * The theme the suite is currently showing, read off the DOM.
 *
 * `initSuiteThemeFromStorage` and the topbar's toggle both write the resolved
 * theme to `<html>` as a `.dark` class plus a `data-theme` attribute, and CSS
 * takes it from there. Anything that is not CSS — a canvas, or a third-party
 * component that wants a `theme` prop — has no cascade to read, so it watches
 * the element instead. Deliberately not `prefers-color-scheme`: the user's own
 * choice, including `system` already resolved, is what is on the element.
 */
export function readEffectiveTheme(): EffectiveSuiteTheme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function subscribeEffectiveTheme(onChange: (theme: EffectiveSuiteTheme) => void): () => void {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
    return () => {};
  }
  const observer = new MutationObserver(() => onChange(readEffectiveTheme()));
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class", "data-theme"],
  });
  return () => observer.disconnect();
}
